import React, { useCallback, useState } from "react";
import {
    IconButton,
    DialogContent,
    DialogActions,
    Button,
    Typography,
} from "@mui/material";
import { Call } from "@mui/icons-material";
import { api } from "../../../api/api";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { useAuth } from "../../../contexts/AuthProvider";

// --- Dialog Component (render at page level) ---

interface IDialerCallDialogProps {
    open: boolean;
    type: "success" | "error";
    message: string;
    onClose: () => void;
}

export const DialerCallDialog: React.FC<IDialerCallDialogProps> = ({
    open,
    type,
    message,
    onClose,
}) => {
    return (
        <Dialog
            size="sm"
            open={open}
            title={type === "error" ? "Call Failed" : "Call Initiated"}
            onClose={onClose} >
            <DialogContent>
                <Typography
                    sx={{
                        color: type === "error" ? "error.main" : "text.primary",
                    }}
                >
                    {message}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Hook (use at page level) ---

interface IDialerState {
    open: boolean;
    type: "success" | "error";
    message: string;
    loading: boolean;
}

export const useDialerCall = () => {
    const [state, setState] = useState<IDialerState>({
        open: false,
        type: "success",
        message: "",
        loading: false,
    });

    const initiateCall = useCallback(async (phoneNumber: string) => {
        setState((prev) => ({ ...prev, loading: true }));
        try {
            const res = await api.post("/general/dialer-call", {
                number: phoneNumber,
            });
            const data = res.data;
            if (data?.data?.ok) {
                setState({
                    open: true,
                    type: "success",
                    message: data?.message || `Call has been initiated to ${phoneNumber}.`,
                    loading: false,
                });
            } else {
                setState({
                    open: true,
                    type: "error",
                    message: data?.message || "Failed to initiate call",
                    loading: false,
                });
            }
        } catch (err: any) {
            setState({
                open: true,
                type: "error",
                message:
                    err?.response?.data?.message ||
                    err?.data?.message ||
                    err?.message ||
                    "Failed to initiate call",
                loading: false,
            });
        }
    }, []);

    const closeDialog = useCallback(() => {
        setState((prev) => ({ ...prev, open: false }));
    }, []);

    return { dialerState: state, initiateCall, closeDialog };
};

// --- Icon Button (render inside row) ---

interface IDialerCallButtonProps {
    phoneNumber: string;
    onCall: (phoneNumber: string) => void;
    disabled?: boolean;
}

export const DialerCallButton: React.FC<IDialerCallButtonProps> = ({
    phoneNumber,
    onCall,
    disabled,
}) => {
    const { isSimDialingEnabled, userSettings } = useAuth();
    console.log("DialerCallButton ==> ", {
        userSettings,
        isSimDialingEnabled
    });

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isSimDialingEnabled) {
            onCall(phoneNumber);
        } else if (phoneNumber) {
            window.open(`tel:${phoneNumber}`, "_self");
        }
    };

    return (
        <IconButton
            size="small"
            onClick={handleClick}
            color="primary"
            disabled={disabled}
            sx={{ p: 0.5 }}
            title={`Call ${phoneNumber}`}
        >
            <Call fontSize="small" />
        </IconButton>
    );
};
